import { Link, Navigate } from "react-router-dom";
import { termsReady } from "../lib/termsOfUse";
import { useAuth } from "../context/AuthContext";
import { homePathForRole } from "../lib/homePath";
import { SIGN_IN_PATH, SIGN_UP_PATH } from "../lib/sessionExpiry";
import { FRONT_DOOR_HERO } from "../lib/frontDoorImagery";
import { TextLink } from "../components/kit/TextLink";
import { BrandMark } from "../components/brand/BrandMark";

/**
 * FrontDoorNew — issue #874, slice S1, the front door redrawn to
 * `FrontDoor.dc.html` (design-baseline-round-5, D44). Lives behind
 * `newUi("signin")` (`App.js`); `Landing.jsx`, the shipped screen, is
 * untouched and keeps serving `/` with the flag off.
 *
 * D44 (Martin, #mvp-1, 2026-09-24): one short front door whose only job is
 * getting a player to log a first match. The "What is this" card, the six
 * feature cards, the two further junior items and the navy footer are gone
 * — the hero, its two ways in and the ownership line (M-07) stay. The demo
 * pair (D101/D49: until go-live) is gone too, as of D205 (Andreas,
 * 2026-10-01, #1023): go-live is now, and there is no public demo.
 *
 * PRESENTATION ONLY. Every behaviour below is the SAME call `Landing.jsx`
 * makes — `useAuth()`'s `user`, the same signed-in redirect via
 * `homePathForRole`. Nothing here calls a different endpoint or widens what
 * the server accepts.
 *
 * CHROME. The drawing draws its own header (mark, Sign in, Create account)
 * outside the app shell — no TopBar, no nav bar (D26/D35: entry screens
 * follow their own drawing's chrome exactly). The mark is `BrandMark`, the
 * one traced logo (#1300, #1153); the header carries it at 24px and the
 * brand panel below the photo band at 88px over "CricketCompass" (D308,
 * D313).
 *
 * AT WIDTH (#1064, D385, D387, D388). Below md (768px) nothing here changes. From md the
 * header is a full-width band whose mark and links sit on the 1040px column's edges (the
 * signed-in bar's gutter), the drawing band stays full bleed at `clamp(200px, 56vw, 300px)` (#1618), and everything under it sits in the 640px column
 * (`index.css`, `.nw-frontdoor`). This page is outside `Layout`, so it carries its own class
 * instead of `nw-shell`.
 *
 * DRAWING BAND (#1618, D440, D442; it was the #883 / D164 photo band). A full-width engineering drawing
 * sits between this header and the headline, read from the single `FRONT_DOOR_HERO` config entry (#896's
 * swappable slot) rather than hard-coded here. It has alt text, no text or number on the image itself (R-001),
 * and loads eagerly at `fetchPriority="high"` since it's the first thing above the fold. Everything else on
 * this screen is exactly as round 5 signed off.
 *
 * HERO LINK, HAND-BUILT (#882 R5). "Create an account" below navigates —
 * it is a link, not a submit — so it stays hand-built rather than routed
 * through the kit's `ActionButton`, which renders a `<button>` only
 * (`kit/ActionButton.jsx`) and has no link form; a `<button>` that
 * navigates is worse for assistive tech than an anchor. Its colours match
 * `ActionButton`'s current ones exactly: `--nw-gold` fill, `--nw-ink`
 * (#0F172A) label (D27/D140).
 */

export default function FrontDoorNew() {
  const { user } = useAuth();

  // Same guard as Landing.jsx, same reasoning: a signed-in visitor never
  // sees this page at all — the drawing's own "already signed in" state is
  // drawn as exactly this absence, not a rendered block.
  if (user) return <Navigate to={homePathForRole(user.role)} replace />;

  return (
    <div
      data-testid="landing"
      className="nw-frontdoor"
      style={{
        minHeight: "100vh",
        background: "var(--nw-page)",
        color: "var(--nw-ink)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Round 5 conformance (token-check) — every element the drawing
          names is checked for its own declared ink token, never inherited
          from `body` (index.css's own `#0B1220`, not `--nw-ink`
          `#0F172A`) — the same rule the kit's type-ramp README states for
          `.nw-*` classes, applied here to two elements with no visible
          text of their own (this root, and the sr-only marker below):
          harmless today because every real text node underneath already
          declares its own colour, but declared here anyway rather than
          left to coincide with the shell's default. */}
      <span data-testid="frontdoor-new-screen" className="sr-only" style={{ color: "var(--nw-ink)" }} aria-hidden="true" />

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          // #1064: a full-width band whose items sit on the 1040px column's edges from md
          // (index.css `.nw-frontdoor`); below md the token is the page gutter, as built.
          padding: "var(--nw-space-topbar-y) var(--nw-band-gutter)",
          background: "var(--nw-surface)",
          borderBottom: "1px solid var(--nw-line)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <BrandMark size={24} />
          <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: 16, color: "var(--nw-navy)" }}>
            Compass
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <TextLink
            as={Link}
            to={SIGN_IN_PATH} data-testid="landing-signin"
            style={{ justifyContent: "flex-end", fontSize: 13, fontWeight: 600, color: "var(--nw-navy)", textDecoration: "none" }}
          >
            Sign in
          </TextLink>
          {/* #1394 (D221 G4, D354): the link is a transparent 44px tap box and the
              36px pill is drawn inside it. `marginBlock` pulls the margin box back
              to the pill's own 36px, so the header keeps its height and the pill
              stays where it was drawn. The link's own outline would trace that invisible
              box, so `.nw-pill-tap` (index.css) puts the focus ring on the pill instead. */}
          <Link
            to={SIGN_UP_PATH} data-testid="landing-signup"
            className="nw-pill-tap"
            style={{
              display: "flex",
              alignItems: "center",
              minHeight: "var(--nw-size-tap)",
              marginBlock: "calc((36px - var(--nw-size-tap)) / 2)",
              textDecoration: "none",
            }}
          >
            <span
              style={{
                display: "flex",
                alignItems: "center",
                minHeight: 36,
                padding: "0 14px",
                background: "var(--nw-navy)",
                color: "#FFFFFF",
                fontSize: 13,
                fontWeight: 600,
                borderRadius: "var(--nw-radius-control)",
              }}
            >
              Create account
            </span>
          </Link>
        </div>
      </div>

      {/* Drawing band (#1618; the #883 photo band before it) — full width, edge to edge, above the
          page's own padding (matches `frontdoor-hero` in
          `O2c-dawn-golden-shadow.dc.html`: `height: 200px; overflow:
          hidden`, no text or number anywhere in the band). The gradient
          overlay fades to `var(--nw-page)` rather than the drawing's own
          literal `#F8FAFC` so the seam still disappears under the
          high-contrast theme (`html.hc-theme` repaints `--nw-page` to
          `#FFFFFF`) — the drawing has no opinion on that theme, so this is
          the one place this slice reads a token instead of copying the
          drawing's hex verbatim. */}
      <div data-testid="frontdoor-hero" className="nw-frontdoor-hero" style={{ position: "relative", overflow: "hidden" }}>
        <img
          src={FRONT_DOOR_HERO.src}
          alt={FRONT_DOOR_HERO.alt}
          width={FRONT_DOOR_HERO.width}
          height={FRONT_DOOR_HERO.height}
          fetchPriority="high"
          loading="eager"
          style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center", display: "block" }}
        />
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(180deg, rgba(248, 250, 252, 0) 55%, var(--nw-page) 100%)",
          }}
        />
      </div>

      <div
        className="nw-frontdoor-body"
        style={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          gap: "var(--nw-space-stack)",
          padding: "var(--nw-space-page)",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div
            data-testid="landing-headline"
            className="nw-title"
            style={{ fontSize: 26, letterSpacing: "-0.6px", lineHeight: 1.15 }}
          >
            Nobody becomes a great player by accident. They become one on purpose, innings by innings.
          </div>
          {/* D-14 in the drawing's own header: "and clubs" is dropped from the
              shipped subline — there is no club account, no club view and no
              club record anywhere in the product. The rest is verbatim. */}
          <div className="nw-lede">
            Cricket has never lacked data. What it's lacked is direction. CricketCompass turns performance
            stats into a clear pathway forward — for players and for the coaches they invite.
          </div>
        </div>

        <Link
          to={SIGN_UP_PATH} data-testid="landing-hero-signup"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "var(--nw-size-action)",
            background: "var(--nw-gold)",
            color: "var(--nw-ink)",
            fontWeight: 700,
            fontSize: 16,
            borderRadius: "var(--nw-radius-control)",
            textDecoration: "none",
          }}
        >
          Create an account
        </Link>

        <Link
          to={SIGN_IN_PATH} data-testid="landing-hero-signin"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 48,
            background: "var(--nw-surface)",
            border: "1px solid var(--nw-line)",
            color: "var(--nw-navy)",
            fontWeight: 600,
            fontSize: 15,
            borderRadius: "var(--nw-radius-control)",
            textDecoration: "none",
          }}
        >
          I already have one
        </Link>

        {/* D308/D313 (#1153): the board's "mark over the name" panel,
            BrandMark.dc.html state "front-door": an 88px mark over the one
            word "CricketCompass" on navy. No separate tagline line (Martin,
            D313: the name is the tagline). D321: it comes after both
            buttons so the call to action keeps its place, and it is a
            480px centred column, not a full-bleed slab at 1440. */}
        <div
          data-testid="frontdoor-brand-panel"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 10,
            padding: "24px 12px",
            background: "var(--nw-navy)",
            borderRadius: 12,
            width: "100%",
            maxWidth: 480,
            alignSelf: "center",
            boxSizing: "border-box",
          }}
        >
          <BrandMark size={88} variant="dark" testId="frontdoor-panel-mark" />
          <div style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: 24, color: "#FFFFFF" }}>CricketCompass</div>
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 12,
            padding: 16,
            background: "var(--nw-surface)",
            border: "var(--nw-border-emph)",
            borderRadius: "var(--nw-radius-card)",
            marginTop: 4,
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <div data-testid="landing-juniors" className="nw-section" style={{ color: "var(--nw-navy)" }}>
              If the player is a junior
            </div>
            <div className="nw-title" style={{ fontSize: 20 }}>
              How a junior's account works
            </div>
          </div>
          {/* M-07 (Martin, thread discord-mvp-1-2026-09-19-1948-q3): the
              ownership claim goes on the landing page only, and this is the
              shipped juniors panel's first bullet, kept word for word — D44
              trims the other two shipped bullets off this short front door. */}
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <div className="nw-body-strong" style={{ color: "var(--nw-navy)" }}>
              The account is the player's, from the first day.
            </div>
            <div className="nw-lede">
              It is set up in their name and it stays theirs — one account and one record, building from
              the first innings on. There is never a second account.
            </div>
          </div>
        </div>

        {/* Issue #1340 (D367.3): D44's footer removal took the privacy link
            with it; this puts it back. Privacy always renders; Terms only
            once the text exists (`termsReady()`). */}
        <p className="nw-help" data-testid="frontdoor-legal-links" style={{ textAlign: "center", margin: 0 }}>
          <TextLink as={Link} inline to="/privacy" className="underline">
            Privacy notice
          </TextLink>
          {termsReady() && (
            <>
              {" · "}
              <TextLink as={Link} inline to="/terms" className="underline">
                Terms of use
              </TextLink>
            </>
          )}
        </p>
      </div>
    </div>
  );
}
